/**
 * Helpers for loading and applying the user's configured email signature.
 */

export const fetchEmailSignature = async (axiosInstance) => {
  if (!axiosInstance) return '';
  try {
    const { data } = await axiosInstance.get('/email-settings/signature');
    return data?.signature || '';
  } catch (error) {
    console.error('Error fetching email signature:', error);
    return '';
  }
};

const stripHtml = (html = '') =>
  String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

/** True if content already contains this signature (avoid double-append). */
export const contentHasSignature = (content, signature) => {
  if (!signature || !String(signature).trim()) return true;
  return String(content || '').includes(String(signature).trim());
};

/**
 * Append signature below body. Empty / blank Quill bodies get signature only.
 */
export const appendSignatureToContent = (content, signature) => {
  if (!signature || !String(signature).trim()) return content || '';
  if (contentHasSignature(content, signature)) return content || '';

  const body = content || '';
  if (!stripHtml(body)) {
    return `<p><br></p><br/>${signature}`;
  }
  return `${body}<br/><br/>${signature}`;
};

export const buildProfilePhotoHtml = (imageUrl, alt = 'Profile photo') => {
  if (!imageUrl) return '';
  const safeAlt = String(alt || 'Profile photo').replace(/"/g, '&quot;');
  return `<img src="${imageUrl}" alt="${safeAlt}" width="80" height="80" style="max-width:80px;max-height:80px;border-radius:8px;object-fit:cover;" />`;
};

/**
 * Convert a remote/signed image URL to a data URI so it embeds in the signature HTML.
 * Falls back to the original URL if conversion fails (e.g. CORS).
 */
export const imageUrlToDataUri = async (imageUrl) => {
  if (!imageUrl) return '';
  if (String(imageUrl).startsWith('data:')) return imageUrl;
  try {
    const response = await fetch(imageUrl, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || '');
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Could not convert image to data URI, using original URL:', error);
    return imageUrl;
  }
};
